import {
  Box,
  Button,
  FormField,
  Input,
  SpaceBetween,
  TextFilter,
} from '@cloudscape-design/components';
import { useState, useEffect, useCallback } from 'react';
import MultiSelectTags from '../tag/MultiSelectTags';
import { useTags } from 'src/store/tags/useTags';
import useUserProfile from 'src/hooks/useUserProfile';
import QuestionCategoryDropdown from '../questionCategory/QuestionCategoryDropdown';
import { SearchAuditQuestionsRequest } from 'src/types/helios/SearchAuditQuestions';
import { useCategories } from 'src/store/categories/useCategories';
import { Tag } from 'src/types/helios/Tags';

interface AuditQuestionFilterViewProps {
  onFilterChange: (filters: SearchAuditQuestionsRequest) => void; // Callback when filters change
  totalQuestions?: number; // Total number of questions (unused)
  completedQuestions?: number; // Number of completed questions (unused)
  initSearchRequest: SearchAuditQuestionsRequest; // Initial filter values
  defaultSearchRequest?: SearchAuditQuestionsRequest; // Default filter values
  /** Optional audit-scoped tags extracted from questions. When provided, these are used
   *  instead of loading all division-level tags, showing only tags relevant to the audit. */
  auditScopedTags?: Tag[];
}

/**
 * AuditQuestionFilterView - Filter panel for audit questions
 * Provides keyword search, question ID, category, and tag filtering options
 */
const AuditQuestionFilterView = ({
  onFilterChange,
  initSearchRequest,
  defaultSearchRequest,
  auditScopedTags,
}: AuditQuestionFilterViewProps) => {
  // Custom hooks for data management
  const { getUserBusinessUnitId, getUserProgramId } = useUserProfile();
  const { loadTags, tagsList, fetchTagsApiStatus } = useTags();
  const { categoriesList, loadCategories } = useCategories();

  // Use audit-scoped tags when provided, otherwise fall back to division-level tags
  const effectiveTags = auditScopedTags ?? tagsList;
  const tagsLoading = auditScopedTags ? false : fetchTagsApiStatus.isLoading;
  const tagsError = auditScopedTags ? null : fetchTagsApiStatus.errorMessage;

  // Filter state initialized with provided values
  const [filters, setFilters] = useState<SearchAuditQuestionsRequest>(initSearchRequest);

  const handleFilterChange = useCallback(
    (newParams: SearchAuditQuestionsRequest) => {
      onFilterChange(newParams);
    },
    [onFilterChange],
  );

  // Load categories once on mount
  useEffect(() => {
    loadCategories({
      businessUnitId: getUserBusinessUnitId()!,
      isActive: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load division-level tags only when audit-scoped tags are not provided
  useEffect(() => {
    if (!auditScopedTags) {
      loadTags({
        businessUnitId: getUserBusinessUnitId()!,
        programId: getUserProgramId()!,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditScopedTags]);

  return (
    <Box>
      <SpaceBetween size="m" direction="vertical">
        {/* Keyword search filter */}
        <TextFilter
          data-testid="keyword-search-filter"
          filteringText={filters?.keyword || ''}
          filteringPlaceholder="Search questions..."
          onChange={({ detail }) => {
            const newParams = {
              ...filters,
              keyword: detail.filteringText ? detail.filteringText : undefined,
            };
            setFilters(newParams);
            handleFilterChange(newParams);
          }}
        />

        {/* Question ID filter */}
        <FormField data-testid="question-id-field" label="Question ID" stretch>
          <Input
            data-testid="question-id-input"
            value={filters?.questionId || ''}
            onChange={({ detail }) => {
              const newParams = {
                ...filters,
                questionId: detail.value ? detail.value : undefined,
              };
              setFilters(newParams);
              handleFilterChange(newParams);
            }}
          />
        </FormField>

        {/* Category filter */}
        <FormField data-testid="Categories" label="Category">
          <QuestionCategoryDropdown
            data-testid="category-dropdown"
            categories={categoriesList}
            selectedCategory={filters?.categories}
            onChange={(selectedCategories) => {
              const newParams = {
                ...filters,
                categories: selectedCategories || [],
              };
              setFilters(newParams);
              handleFilterChange(newParams);
            }}
          />
        </FormField>

        {/* Tags filter with multi-select */}
        <FormField data-testid="tags-field" label="Tags">
          <MultiSelectTags
            data-testid="multi-select-tags"
            tags={effectiveTags}
            isLoading={tagsLoading}
            error={tagsError}
            selectedTagValues={Object.entries(filters?.tagFilters || {}).map(
              ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
            )}
            onItemSelection={(selectedTags) => {
              // Convert selected tags array to map format
              const tagsMap: Record<string, string[]> = {};
              selectedTags.forEach((tag) => {
                tagsMap[tag.tagGroupName] = tag.tagValues;
              });
              const newParams = { ...filters, tagFilters: tagsMap };
              setFilters(newParams);
              handleFilterChange(newParams);
            }}
          />
        </FormField>

        {/* Reset filters button */}
        <Button
          data-testid="reset-filters-button"
          onClick={() => {
            const resetFilters = defaultSearchRequest || initSearchRequest;
            setFilters(resetFilters);
            handleFilterChange(resetFilters);
          }}
        >
          Reset Filters
        </Button>
      </SpaceBetween>
    </Box>
  );
};

export default AuditQuestionFilterView;
